import React, { useEffect, useState, useContext } from "react";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { Link } from "react-router-dom";
import { DeleteConfirm } from "../../components/modal/deleteConfirm";
import { TrashIcon, PlusIcon, MapPinIcon } from "@heroicons/react/24/outline";

import { Button } from "../../components/button";
import { PageHeading } from "../../components/pageHeading";
import { AuthContext } from "../../contexts/AuthContext";
import { SitesCustomerFilter } from "./customerFilter";
import { SiteDetailsMeta } from "./siteDetailsMeta";
import { Table } from "../../components/table";

function SiteSettings() {
  const { authContext, fetchUserData } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();
  const allowedSites = authContext.organization.planFeatures.Sites;
  const [siteCount, setSiteCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState({
    identifier: "",
    id: "",
  });
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);

  /// Customers dropdown ///
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const handleSelectedCustomers = (optionId) => {
    setSelectedCustomers((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedCustomers = () => {
    setSelectedCustomers([]);
  };

  const toggleState = (setState, state) => {
    setState(!state);
  };

  /// Delete modal ///
  const handleDeleteClick = (identifier, id) => {
    setSelectedDeleteItem({ identifier, id });
    toggleState(setIsDeleteOpen, isDeleteOpen);
  };

  const deleteUser = (siteId) => {
    return authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites/delete/${siteId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      },
    ).then((response) => response.json());
  };

  const [deleteSuccess, setDeleteSuccess] = useState({
    displaying: false,
    message: "Site has been deleted",
  });

  const handleDeleteSuccess = () => {
    fetchSites();

    setDeleteSuccess({
      displaying: true,
      message: "Site has been deleted",
    });
    setTimeout(() => {
      setDeleteSuccess({ displaying: false, message: deleteSuccess.message });
    }, 3000);
  };

  // const [fetchFlag, setFetchFlag] = useState(false);

  const handleSuccess = async () => {
    setIsLoading(true); // Show loading spinner
    await fetchSites();
    await fetchUserData();
  };

  // useEffect(() => {
  //   setIsLoading(true);
  //   fetchSites();
  // }, [fetchFlag]);

  useEffect(() => {
    if (orgId) {
      setIsLoading(true);
      fetchSites();
    }
  }, [orgId, selectedCustomers]);

  /// Fetch sites ///
  const [backendData, setBackendData] = useState([{}]);
  const fetchSites = async () => {
    // Early return if customerId is not available
    if (!orgId) return;

    // Assume selectedIds is an array containing the selected IDs from the dropdown
    const selectedIds = selectedCustomers;

    // Construct the URL with or without the ids parameter
    let url = `${process.env.REACT_APP_API_URL}/v1/sites?org_id=${orgId}&customer_ids=${selectedIds}&include_details=true`;
    if (selectedIds.length > 0) {
      const idsParam = selectedIds.join(",");
      url += `&ids=${idsParam}`;
    }

    const fetchPromise = authFetch(url)
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          setBackendData(data); // Update state with the fetched data
          setSiteCount(data.length);
        } else {
          // Handle HTTP errors e.g., 404, 500 etc.
          console.error("HTTP Error: ", response.statusText);
          // Optionally, update state to display an error message
        }
      })
      .catch((error) => {
        // Handle network errors, parsing errors, etc.
        console.error("Fetching error: ", error.message);
        // Optionally, update state to display an error message
      });

    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));

    Promise.all([fetchPromise, timerPromise]).then(() => {
      setIsLoading(false);
    });
  };

  // useEffect(() => {
  //   fetchSites();
  // }, [orgId, selectedCustomers]);

  /// Table columns ///
  const columns = React.useMemo(
    () => [
      {
        Header: "Site name",
        accessor: "site_name",
        // Use a custom cell to render the link
        Cell: ({ row }) => (
          <Link
            to={`/sites/${row.original.id}`}
            className="text-gray-800 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:underline capitalize cursor-pointer"
          >
            {row.values.site_name}
          </Link>
        ),
      },
      {
        Header: "Address",
        accessor: "street",
      },
      {
        Header: "Customer",
        accessor: "customer_name",
      },
      {
        Header: "",
        id: "delete",
        accessor: (str) => "delete",
        Cell: ({ row }) => (
          <button
            className="pl-6 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-150 block"
            onClick={() =>
              handleDeleteClick(row.original.site_name, row.original.id)
            }
          >
            <TrashIcon className="h-4 w-4 shrink-0" />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <main className="py-6">
        <div className="px-4 sm:px-6 lg:px-6">
          <PageHeading title="Sites">
            <Button
              icon={PlusIcon}
              onClick={() => {
                toggleState(setIsDetailsOpen, isDetailsOpen);
              }}
              disabled={allowedSites > 0 && allowedSites <= siteCount}
            >
              {`Create site${
                allowedSites > 0 && siteCount
                  ? ` (${siteCount}/${allowedSites})`
                  : ""
              }`}
            </Button>
          </PageHeading>
          <div className="flex items-center gap-x-4 ml-1 mb-1">
            <SitesCustomerFilter
              selected={selectedCustomers}
              onUpdate={handleSelectedCustomers}
              onClear={clearSelectedCustomers}
            />
          </div>
          <div>
            <Table
              data={backendData}
              columns={columns}
              defaultSort="site_name"
              isLoading={isLoading}
              emptyState={
                <div className="flex flex-col items-center justify-center h-72">
                  <MapPinIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                  <p className="mt-3 mb-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                    No sites yet
                  </p>
                  <Button
                    onClick={() => {
                      toggleState(setIsDetailsOpen, isDetailsOpen);
                    }}
                  >
                    Create site
                  </Button>
                </div>
              }
            />
          </div>
        </div>
      </main>
      <DeleteConfirm
        isOpen={isDeleteOpen}
        toggleOpen={() => toggleState(setIsDeleteOpen, isDeleteOpen)}
        selectedDeleteItem={selectedDeleteItem}
        handleDeleteSuccess={handleDeleteSuccess}
        onDelete={deleteUser}
      />
      <SiteDetailsMeta
        isOpen={isDetailsOpen}
        toggleOpen={() => toggleState(setIsDetailsOpen, isDetailsOpen)}
        selectedSiteId={selectedSiteId}
        isAddMode={true}
        siteData={backendData}
        updateSiteData={fetchSites}
        onSuccess={handleSuccess}
      />
    </div>
  );
}

export { SiteSettings };
